import * as React from "react";
import { setNestedProperty } from "./setNestedProperty";
import { scrollToFirstErrorField } from "@neworbit/simpleui-utils";

export interface SaveDispatchProps<TModel> {
    save: (state: TModel) => Promise<void>;
}

export type ValidationObject<TModel> = {
    [K in keyof TModel]?: boolean;
};

export interface FormState<TModel> {
    loading?: boolean;
    values: TModel;
    valid: ValidationObject<TModel>;
    showErrors?: boolean;
}

export interface FormBaseComponent <TModel, TProps = {}, TState extends FormState<TModel> = FormState<TModel>>
extends React.Component<TProps & SaveDispatchProps<TModel>, TState> {
     onSaved?: () => void;
}

export abstract class FormBaseComponent<TModel, TProps = {}, TState extends FormState<TModel> = FormState<TModel>>
    extends React.Component<TProps & SaveDispatchProps<TModel>, TState>
    implements FormBaseComponent<TModel, TProps, TState> {

    private mounted: boolean = false;

    public componentDidMount() {
        this.mounted = true;
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    protected updateProperty<TValue>(prop: keyof TModel, value: TValue, valid?: boolean): void {
        this.setState(current =>  {
            return {
                values: Object.assign({}, current.values, { [prop]: value }),
                valid: Object.assign({}, current.valid, { [prop]: valid })
            };
        });
    }

    protected updateNestedProperty<TValue>(prop: string, value: TValue, valid?: boolean): void {
        this.setState(current => {
            return {
                values: setNestedProperty(current.values, prop, value),
                valid: Object.assign({}, current.valid, { [prop]: valid })
            };
        });
    }

    protected getUpdate(prop: keyof TModel) {
        return (value: any, valid: boolean) => this.updateProperty(prop, value, valid);
    }

    protected getNestedUpdate(prop: string) {
        return (value: any, valid: boolean) => this.updateNestedProperty(prop, value, valid);
    }

    protected handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        if (!this.valid()) {
            this.setState(
                { showErrors: true },
                () => setTimeout(() => scrollToFirstErrorField(), 100));
            return;
        }

        this.setState({ loading: true });
        try {
            await this.props.save(this.state.values);

            if (this.onSaved) {
                this.onSaved();
            }

        } finally {
            if (this.mounted) {
                this.setState({ loading: false });
            }
        }
    }

    protected valid() {
        return !Object.keys(this.state.valid).some(k => !(this.state.valid as any)[k]);
    }
}
